import React from 'react';
import { FeatureAccessGate } from '../components/FeatureAccessGate';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

export const FeatureAccessTestPage: React.FC = () => {
  const {
    loading,
    error,
    effectiveTier,
    isSubscriptionActive,
    availableFeatures,
    data: subscriptionData
  } = useFeatureAccess();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-blue-100">
        <div className="animate-pulse text-blue-600 text-lg">Loading subscription status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-red-50 to-red-100">
        <div className="text-red-600 text-lg">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Feature Access Control Test</h1>
        
        {/* Current Subscription Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Subscription Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Current Tier</p>
              <p className="text-lg font-medium text-gray-900 capitalize">{effectiveTier}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Subscription Status</p>
              <p className="text-lg font-medium text-gray-900 capitalize">
                {isSubscriptionActive ? 'Active' : 'Inactive'}
              </p>
            </div>
            {subscriptionData && (
              <>
                <div>
                  <p className="text-sm text-gray-500">Plan ID</p>
                  <p className="text-lg font-medium text-gray-900">{subscriptionData.plan_id ?? subscriptionData.plan ?? 'none'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Available Features</p>
                  <p className="text-lg font-medium text-gray-900">
                    {availableFeatures().length} features
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Feature Access Tests */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Feature Access Tests</h2>
          
          {/* Free Tier Features */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Free Tier Features</h3>
            <div className="space-y-4">
              <FeatureAccessGate feature="case1">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  ✓ Case 1 is accessible
                </div>
              </FeatureAccessGate>
              <FeatureAccessGate feature="case10">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  ✓ Case 10 is accessible
                </div>
              </FeatureAccessGate>
            </div>
          </div>

          {/* Basic Tier Features */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assessment Features</h3>
            <div className="space-y-4">
              <FeatureAccessGate feature="case2">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  ✓ Case 2 is accessible
                </div>
              </FeatureAccessGate>
              <FeatureAccessGate feature="ecosystemSolverManualEntry">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  ✓ Manual Entry is accessible
                </div>
              </FeatureAccessGate>
            </div>
          </div>

          {/* Advanced Assessment Features */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced Assessment Features</h3>
            <div className="space-y-4">
              <FeatureAccessGate feature="seaWolfSolver">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  ✓ SeaWolf Solver is accessible
                </div>
              </FeatureAccessGate>
              <FeatureAccessGate feature="ecosystemSolverSnapAndSolve">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  ✓ Snap and Solve is accessible
                </div>
              </FeatureAccessGate>
            </div>
          </div>

          {/* Available Features List */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">All Available Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableFeatures().map((feature) => (
                <div key={feature} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <span className="text-blue-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 
